import { Component, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, take, takeUntil, tap, catchError } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import _ from 'lodash';
import { MatOptionSelectionChange } from '@angular/material/core';


export interface AutocompleteOption {
  label: string;
  value: any;
}

@Component({
  selector: 'formly-autocomplete-type',
  template: `
      <!-- visible control -->
      <ng-container *ngIf="multiple; else singleSelect">
          <mat-chip-grid #chipList>
              <mat-chip-option
                      *ngFor="let item of itemList"
                      [selectable]="selectable"
                      [removable]="removable"
                      (removed)="remove(item)">
                  {{item}}
                  <mat-icon matChipRemove *ngIf="removable">cancel</mat-icon>
              </mat-chip-option>
              <input #itemInput
                     [placeholder]="to?.placeholder"
                     [formControl]="myControl"
                     [matAutocomplete]="auto"
                     [matChipInputFor]="chipList"
                     [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                     (matChipInputTokenEnd)="add($event)">
          </mat-chip-grid>
      </ng-container>
      <ng-template #singleSelect>
          <input matInput
                 type="text"
                 [placeholder]="to?.placeholder"
                 [formlyAttributes]="field"
                 [formControl]="myControl"
                 [matAutocomplete]="auto">
      </ng-template>
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn.bind(this)"
                        (optionSelected)="handleOptionSelected($event.option.value)">
          <ng-container *ngIf="hasSubGroup; else default">
              <mat-optgroup *ngFor="let option of options$ | async" [label]="option.teamName">
                  <mat-option (onSelectionChange)="optionSelected($event, name)" *ngFor="let name of option.teamMembers"
                              [value]="name.value">
                      {{name.label}}
                  </mat-option>
              </mat-optgroup>
          </ng-container>
          <ng-template #default>
              <mat-option *ngFor="let option of options$ | async" [value]="option">
                  {{ option.label }}
              </mat-option>
          </ng-template>
      </mat-autocomplete>
      <!-- formly control -->
      <input [formControl]="formControl" type="hidden">
  `,
  standalone: false
})
export class FormlyAutocompleteTypeComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChild('itemInput') itemInput: ElementRef<HTMLInputElement>;

  getOptions: (search: string) => Observable<AutocompleteOption[]> | [];
  getSelected: (value: any) => Observable<AutocompleteOption>;
  options$: Observable<AutocompleteOption[]>;
  myControl: UntypedFormControl = new UntypedFormControl();
  destroyed = new Subject();
  removable = true;
  hasSubGroup = false;
  multiple = false;
  selectedSubgroupId: number;
  selectedSubgroupName: string;
  itemList: string[] = [];

  ngOnInit() {
    this.initSelected();
    this.initOptions();
  }

  ngOnDestroy() {
    this.destroyed.next(true);
  }

  optionSelected(event: MatOptionSelectionChange, selected: { value: number, label: string }) {
    this.selectedSubgroupId = selected.value;
    this.selectedSubgroupName = selected.label;
    this.formControl.setValue(selected.value);
    this.formControl.markAsDirty();
  }


  displayFn(option: AutocompleteOption): string {
    return this.selectedSubgroupName ? this.selectedSubgroupName : (option?.label || '');
  }

  handleOptionSelected(option: AutocompleteOption) {
    if (this.multiple) {
      this.itemList.push(option.label);
      this.itemInput.nativeElement.value = '';
      this.formControl.setValue(this.itemList);
    } else {
      const selectedVal = this.hasSubGroup ? this.selectedSubgroupId : option.value;
      this.formControl.setValue(selectedVal);
      this.formControl.markAsDirty();
    }
  }

  private initSelected() {
    if (!this.to.getSelected) {
      return;
    }
    this.getSelected = this.to.getSelected;
    if (this.formControl.value) {
      this.getSelected(this.formControl.value)
        .pipe(take(1))
        .subscribe(option => {
          this.myControl.setValue(option);
        });
    }
  }

  private initOptions() {
    this.hasSubGroup = this.to.hasSubGroup;
    this.multiple = this.to.multiple;

    // setup options observable
    const optionsBS = new BehaviorSubject<AutocompleteOption[]>([]);
    this.options$ = optionsBS.asObservable();

    // respond to value changes
    this.getOptions = this.to.getOptions;
    this.myControl = new UntypedFormControl();
    this.myControl.valueChanges
      .pipe(
        takeUntil(this.destroyed),
        filter(search => typeof search === 'string'),
        tap(search => {
          this.formControl.setValue(null); // throw away previous value
          this.formControl.markAsTouched(); // force ng validation
          optionsBS.next([]); // clear previous results
        }),
        debounceTime(500),
        switchMap(search => this.getOptions(search)),
        catchError(() => of([])),
        tap(options => optionsBS.next(options)),
      )
      .subscribe();
  }

  private remove(item: string){
    const index = this.itemList.indexOf(item);
    if (index >= 0) {
      this.itemList.splice(index, 1);
    }
    this.formControl.setValue(this.itemList);
  }
}
